import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Switch } from "../components/ui/switch";
import { toast } from "sonner";
import axios from "axios";
import { 
  ArrowRight, Plus, Search, Download, Upload, Trash2, 
  Edit, Power, Filter, FileJson, FileSpreadsheet, Lock,
  ChevronLeft, ChevronRight, MoreVertical
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CATEGORIES = [
  "تاريخ", "جغرافيا", "علوم", "رياضة",
  "فن وثقافة", "دين", "لغة عربية", "معلومات عامة"
];

const DIFFICULTY_GROUPS = ["سهل", "متوسط", "صعب"];

export default function AdminPage() {
  const navigate = useNavigate();
  
  // Auth State
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState("");
  const [authLoading, setAuthLoading] = useState(false);
  
  // Questions State
  const [questions, setQuestions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  
  // Filters
  const [filters, setFilters] = useState({
    category: "",
    difficulty_level: "",
    difficulty_group: "",
    is_active: "",
    search: ""
  });
  const [page, setPage] = useState(0);
  const pageSize = 20;
  
  // Modal State
  const [showQuestionModal, setShowQuestionModal] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletingQuestion, setDeletingQuestion] = useState(null);
  const [showImportModal, setShowImportModal] = useState(false);
  
  // Form State
  const [formData, setFormData] = useState({
    question_text: "",
    option_a: "",
    option_b: "",
    option_c: "",
    option_d: "",
    correct_option: "A",
    category: "معلومات عامة",
    difficulty_level: 1,
    explanation: "",
    is_active: true
  });

  // Authentication
  const handleAuth = async (e) => {
    e.preventDefault();
    setAuthLoading(true);
    
    try {
      await axios.post(`${API}/admin/auth`, { password });
      setIsAuthenticated(true);
      toast.success("تم التحقق بنجاح");
    } catch (error) {
      toast.error("كلمة المرور غير صحيحة");
    } finally {
      setAuthLoading(false);
    }
  };

  // Fetch Questions
  const fetchQuestions = async () => {
    setIsLoading(true);
    
    try {
      const params = {
        skip: page * pageSize,
        limit: pageSize
      };
      
      if (filters.category) params.category = filters.category;
      if (filters.difficulty_level) params.difficulty_level = parseInt(filters.difficulty_level);
      if (filters.difficulty_group) params.difficulty_group = filters.difficulty_group;
      if (filters.is_active !== "") params.is_active = filters.is_active === "true";
      if (filters.search) params.search = filters.search;
      
      const [questionsRes, countRes] = await Promise.all([
        axios.get(`${API}/questions`, { params }),
        axios.get(`${API}/questions/count`, { params })
      ]);
      
      setQuestions(questionsRes.data);
      setTotalCount(countRes.data.count);
    } catch (error) {
      console.error("Error fetching questions:", error);
      toast.error("حدث خطأ في تحميل الأسئلة");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (isAuthenticated) {
      fetchQuestions();
    }
  }, [isAuthenticated, page, filters]);

  // Seed Questions
  const handleSeedQuestions = async () => {
    try {
      const response = await axios.post(`${API}/seed-questions`);
      toast.success(response.data.message);
      fetchQuestions();
    } catch (error) {
      toast.error("حدث خطأ في إضافة الأسئلة");
    }
  };

  // Create/Update Question
  const handleSaveQuestion = async (e) => {
    e.preventDefault();
    
    try {
      if (editingQuestion) {
        await axios.put(`${API}/questions/${editingQuestion.id}`, formData);
        toast.success("تم تحديث السؤال بنجاح");
      } else {
        await axios.post(`${API}/questions`, formData);
        toast.success("تم إضافة السؤال بنجاح");
      }
      
      setShowQuestionModal(false);
      setEditingQuestion(null);
      resetForm();
      fetchQuestions();
    } catch (error) {
      toast.error("حدث خطأ في حفظ السؤال");
    }
  };

  // Delete Question
  const handleDeleteQuestion = async () => {
    if (!deletingQuestion) return;
    
    try {
      await axios.delete(`${API}/questions/${deletingQuestion.id}`);
      toast.success("تم حذف السؤال بنجاح");
      setShowDeleteModal(false);
      setDeletingQuestion(null);
      fetchQuestions();
    } catch (error) {
      toast.error("حدث خطأ في حذف السؤال");
    }
  };

  // Toggle Question Active
  const handleToggleActive = async (questionId) => {
    try {
      const response = await axios.patch(`${API}/questions/${questionId}/toggle`);
      toast.success(response.data.is_active ? "تم تفعيل السؤال" : "تم إلغاء تفعيل السؤال");
      fetchQuestions();
    } catch (error) {
      toast.error("حدث خطأ");
    }
  };

  // Import Questions
  const handleImport = async (e, type) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    const formData = new FormData();
    formData.append("file", file);
    
    try {
      const endpoint = type === "json" ? "import/json" : "import/csv";
      const response = await axios.post(`${API}/questions/${endpoint}`, formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      toast.success(response.data.message);
      setShowImportModal(false);
      fetchQuestions();
    } catch (error) {
      toast.error("حدث خطأ في الاستيراد");
    }
  };

  // Export Questions
  const handleExport = async (type) => {
    try {
      const endpoint = type === "json" ? "export/json" : "export/csv";
      const response = await axios.get(`${API}/questions/${endpoint}`, {
        responseType: "blob"
      });
      
      const url = window.URL.createObjectURL(response.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `questions.${type}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success("تم التصدير بنجاح");
    } catch (error) {
      toast.error("حدث خطأ في التصدير");
    }
  };

  const resetForm = () => {
    setFormData({
      question_text: "",
      option_a: "",
      option_b: "",
      option_c: "",
      option_d: "",
      correct_option: "A",
      category: "معلومات عامة",
      difficulty_level: 1,
      explanation: "",
      is_active: true
    });
  };

  const openEditModal = (question) => {
    setEditingQuestion(question);
    setFormData({
      question_text: question.question_text,
      option_a: question.option_a,
      option_b: question.option_b,
      option_c: question.option_c,
      option_d: question.option_d,
      correct_option: question.correct_option,
      category: question.category,
      difficulty_level: question.difficulty_level,
      explanation: question.explanation || "",
      is_active: question.is_active
    });
    setShowQuestionModal(true);
  };

  // Auth Screen
  if (!isAuthenticated) {
    return (
      <div className="admin-panel flex items-center justify-center">
        <div className="w-full max-w-md p-8">
          <div className="text-center mb-8">
            <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-slate-800 flex items-center justify-center">
              <Lock className="w-10 h-10 text-neon-cyan" />
            </div>
            <h1 className="font-heading text-3xl text-white mb-2">لوحة التحكم</h1>
            <p className="text-slate-400">أدخل كلمة المرور للوصول</p>
          </div>
          
          <form onSubmit={handleAuth} className="space-y-4">
            <div>
              <Input
                data-testid="admin-password-input"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="كلمة المرور"
                className="bg-slate-800 border-slate-700 text-white text-center text-lg py-6"
                dir="ltr"
              />
            </div>
            
            <Button
              data-testid="admin-login-btn"
              type="submit"
              disabled={authLoading || !password}
              className="w-full py-6 bg-neon-cyan text-slate-900 hover:bg-neon-cyan-dim font-bold text-lg"
            >
              {authLoading ? "جاري التحقق..." : "دخول"}
            </Button>
          </form>
          
          <Button
            onClick={() => navigate("/")}
            variant="ghost"
            className="w-full mt-4 text-slate-400 hover:text-white"
          >
            <ArrowRight className="ml-2 w-4 h-4" />
            العودة للصفحة الرئيسية
          </Button>
        </div>
      </div>
    );
  }

  // Admin Dashboard
  return (
    <div className="admin-panel min-h-screen" dir="rtl">
      {/* Header */}
      <div className="bg-slate-900 border-b border-slate-800 p-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              data-testid="back-to-home"
              onClick={() => navigate("/")}
              variant="ghost"
              className="text-slate-400 hover:text-white"
            >
              <ArrowRight className="ml-2 w-4 h-4" />
              رجوع
            </Button>
            <h1 className="font-heading text-2xl text-white">إدارة الأسئلة</h1>
          </div>
          
          <div className="flex items-center gap-2">
            <Button
              data-testid="seed-questions-btn"
              onClick={handleSeedQuestions}
              variant="outline"
              className="border-slate-600 text-slate-300 hover:bg-slate-800"
            >
              إضافة أسئلة أولية
            </Button>
            
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" className="border-slate-600 text-slate-300 hover:bg-slate-800">
                  <Upload className="ml-2 w-4 h-4" />
                  استيراد
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="bg-slate-800 border-slate-700">
                <DropdownMenuItem 
                  className="text-white hover:bg-slate-700 cursor-pointer"
                  onClick={() => setShowImportModal(true)}
                >
                  <FileJson className="ml-2 w-4 h-4" />
                  استيراد من ملف
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" className="border-slate-600 text-slate-300 hover:bg-slate-800">
                  <Download className="ml-2 w-4 h-4" />
                  تصدير
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="bg-slate-800 border-slate-700">
                <DropdownMenuItem 
                  className="text-white hover:bg-slate-700 cursor-pointer"
                  onClick={() => handleExport("json")}
                >
                  <FileJson className="ml-2 w-4 h-4" />
                  JSON
                </DropdownMenuItem>
                <DropdownMenuItem 
                  className="text-white hover:bg-slate-700 cursor-pointer"
                  onClick={() => handleExport("csv")}
                >
                  <FileSpreadsheet className="ml-2 w-4 h-4" />
                  CSV
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            
            <Button
              data-testid="add-question-btn"
              onClick={() => {
                resetForm();
                setEditingQuestion(null);
                setShowQuestionModal(true);
              }}
              className="bg-neon-cyan text-slate-900 hover:bg-neon-cyan-dim"
            >
              <Plus className="ml-2 w-4 h-4" />
              إضافة سؤال
            </Button>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="max-w-7xl mx-auto p-4">
        <div className="admin-card p-4 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            <div className="relative">
              <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
              <Input
                data-testid="search-input"
                placeholder="بحث..."
                value={filters.search}
                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                className="bg-slate-800 border-slate-700 text-white pr-10"
              />
            </div>
            
            <Select
              value={filters.category}
              onValueChange={(value) => setFilters({ ...filters, category: value })}
            >
              <SelectTrigger data-testid="filter-category" className="bg-slate-800 border-slate-700 text-white">
                <SelectValue placeholder="جميع الفئات" />
              </SelectTrigger>
              <SelectContent className="bg-slate-800 border-slate-700">
                <SelectItem value=" " className="text-white">جميع الفئات</SelectItem>
                {CATEGORIES.map(cat => (
                  <SelectItem key={cat} value={cat} className="text-white">{cat}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <Select
              value={filters.difficulty_level}
              onValueChange={(value) => setFilters({ ...filters, difficulty_level: value })}
            >
              <SelectTrigger data-testid="filter-level" className="bg-slate-800 border-slate-700 text-white">
                <SelectValue placeholder="جميع المستويات" />
              </SelectTrigger>
              <SelectContent className="bg-slate-800 border-slate-700">
                <SelectItem value=" " className="text-white">جميع المستويات</SelectItem>
                {[...Array(15)].map((_, i) => (
                  <SelectItem key={i + 1} value={String(i + 1)} className="text-white">
                    المستوى {i + 1}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <Select
              value={filters.difficulty_group}
              onValueChange={(value) => setFilters({ ...filters, difficulty_group: value })}
            >
              <SelectTrigger data-testid="filter-group" className="bg-slate-800 border-slate-700 text-white">
                <SelectValue placeholder="جميع الصعوبات" />
              </SelectTrigger>
              <SelectContent className="bg-slate-800 border-slate-700">
                <SelectItem value=" " className="text-white">جميع الصعوبات</SelectItem>
                {DIFFICULTY_GROUPS.map(group => (
                  <SelectItem key={group} value={group} className="text-white">{group}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <Select
              value={filters.is_active}
              onValueChange={(value) => setFilters({ ...filters, is_active: value })}
            >
              <SelectTrigger data-testid="filter-status" className="bg-slate-800 border-slate-700 text-white">
                <SelectValue placeholder="جميع الحالات" />
              </SelectTrigger>
              <SelectContent className="bg-slate-800 border-slate-700">
                <SelectItem value=" " className="text-white">جميع الحالات</SelectItem>
                <SelectItem value="true" className="text-white">مفعل</SelectItem>
                <SelectItem value="false" className="text-white">معطل</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Questions Table */}
        <div className="admin-card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-slate-800">
                  <th className="text-right p-3 text-slate-300 font-medium text-sm">السؤال</th>
                  <th className="text-right p-3 text-slate-300 font-medium text-sm">الفئة</th>
                  <th className="text-right p-3 text-slate-300 font-medium text-sm">المستوى</th>
                  <th className="text-right p-3 text-slate-300 font-medium text-sm">الصعوبة</th>
                  <th className="text-right p-3 text-slate-300 font-medium text-sm">الحالة</th>
                  <th className="text-right p-3 text-slate-300 font-medium text-sm">إجراءات</th>
                </tr>
              </thead>
              <tbody>
                {isLoading ? (
                  <tr>
                    <td colSpan={6} className="text-center py-8 text-slate-400">
                      جاري التحميل...
                    </td>
                  </tr>
                ) : questions.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="text-center py-8 text-slate-400">
                      لا توجد أسئلة
                    </td>
                  </tr>
                ) : (
                  questions.map((q) => (
                    <tr key={q.id} className="border-b border-slate-800 hover:bg-slate-800/50">
                      <td className="p-3 text-white text-sm max-w-xs truncate">
                        {q.question_text}
                      </td>
                      <td className="p-3 text-slate-300 text-sm">{q.category}</td>
                      <td className="p-3 text-slate-300 text-sm font-numbers">{q.difficulty_level}</td>
                      <td className="p-3">
                        <span className={`
                          px-2 py-1 rounded-full text-xs
                          ${q.difficulty_group === 'سهل' ? 'bg-green-500/20 text-green-400' : ''}
                          ${q.difficulty_group === 'متوسط' ? 'bg-yellow-500/20 text-yellow-400' : ''}
                          ${q.difficulty_group === 'صعب' ? 'bg-red-500/20 text-red-400' : ''}
                        `}>
                          {q.difficulty_group}
                        </span>
                      </td>
                      <td className="p-3">
                        <Switch
                          data-testid={`toggle-${q.id}`}
                          checked={q.is_active}
                          onCheckedChange={() => handleToggleActive(q.id)}
                        />
                      </td>
                      <td className="p-3">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm" className="text-slate-400 hover:text-white">
                              <MoreVertical className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent className="bg-slate-800 border-slate-700">
                            <DropdownMenuItem 
                              className="text-white hover:bg-slate-700 cursor-pointer"
                              onClick={() => openEditModal(q)}
                            >
                              <Edit className="ml-2 w-4 h-4" />
                              تعديل
                            </DropdownMenuItem>
                            <DropdownMenuItem 
                              className="text-red-400 hover:bg-slate-700 cursor-pointer"
                              onClick={() => {
                                setDeletingQuestion(q);
                                setShowDeleteModal(true);
                              }}
                            >
                              <Trash2 className="ml-2 w-4 h-4" />
                              حذف
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          
          {/* Pagination */}
          <div className="flex items-center justify-between p-4 border-t border-slate-800">
            <p className="text-slate-400 text-sm">
              عرض {page * pageSize + 1} - {Math.min((page + 1) * pageSize, totalCount)} من {totalCount}
            </p>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(p => Math.max(0, p - 1))}
                disabled={page === 0}
                className="border-slate-700 text-slate-300"
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(p => p + 1)}
                disabled={(page + 1) * pageSize >= totalCount}
                className="border-slate-700 text-slate-300"
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Add/Edit Question Modal */}
      <Dialog open={showQuestionModal} onOpenChange={setShowQuestionModal}>
        <DialogContent className="bg-slate-900 border-slate-700 text-white max-w-2xl max-h-[90vh] overflow-y-auto" aria-describedby="question-modal-description">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">
              {editingQuestion ? "تعديل السؤال" : "إضافة سؤال جديد"}
            </DialogTitle>
          </DialogHeader>
          <p id="question-modal-description" className="sr-only">نموذج إضافة أو تعديل سؤال</p>
          
          <form onSubmit={handleSaveQuestion} className="space-y-4">
            <div>
              <Label className="text-slate-300">السؤال</Label>
              <Textarea
                data-testid="question-text-input"
                value={formData.question_text}
                onChange={(e) => setFormData({ ...formData, question_text: e.target.value })}
                className="bg-slate-800 border-slate-700 text-white mt-1"
                rows={3}
                required
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-slate-300">الخيار A</Label>
                <Input
                  data-testid="option-a-input"
                  value={formData.option_a}
                  onChange={(e) => setFormData({ ...formData, option_a: e.target.value })}
                  className="bg-slate-800 border-slate-700 text-white mt-1"
                  required
                />
              </div>
              <div>
                <Label className="text-slate-300">الخيار B</Label>
                <Input
                  data-testid="option-b-input"
                  value={formData.option_b}
                  onChange={(e) => setFormData({ ...formData, option_b: e.target.value })}
                  className="bg-slate-800 border-slate-700 text-white mt-1"
                  required
                />
              </div>
              <div>
                <Label className="text-slate-300">الخيار C</Label>
                <Input
                  data-testid="option-c-input"
                  value={formData.option_c}
                  onChange={(e) => setFormData({ ...formData, option_c: e.target.value })}
                  className="bg-slate-800 border-slate-700 text-white mt-1"
                  required
                />
              </div>
              <div>
                <Label className="text-slate-300">الخيار D</Label>
                <Input
                  data-testid="option-d-input"
                  value={formData.option_d}
                  onChange={(e) => setFormData({ ...formData, option_d: e.target.value })}
                  className="bg-slate-800 border-slate-700 text-white mt-1"
                  required
                />
              </div>
            </div>
            
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label className="text-slate-300">الإجابة الصحيحة</Label>
                <Select
                  value={formData.correct_option}
                  onValueChange={(value) => setFormData({ ...formData, correct_option: value })}
                >
                  <SelectTrigger data-testid="correct-option-select" className="bg-slate-800 border-slate-700 text-white mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-800 border-slate-700">
                    {['A', 'B', 'C', 'D'].map(opt => (
                      <SelectItem key={opt} value={opt} className="text-white">{opt}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label className="text-slate-300">الفئة</Label>
                <Select
                  value={formData.category}
                  onValueChange={(value) => setFormData({ ...formData, category: value })}
                >
                  <SelectTrigger data-testid="category-select" className="bg-slate-800 border-slate-700 text-white mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-800 border-slate-700">
                    {CATEGORIES.map(cat => (
                      <SelectItem key={cat} value={cat} className="text-white">{cat}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label className="text-slate-300">المستوى (1-15)</Label>
                <Select
                  value={String(formData.difficulty_level)}
                  onValueChange={(value) => setFormData({ ...formData, difficulty_level: parseInt(value) })}
                >
                  <SelectTrigger data-testid="level-select" className="bg-slate-800 border-slate-700 text-white mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-800 border-slate-700">
                    {[...Array(15)].map((_, i) => (
                      <SelectItem key={i + 1} value={String(i + 1)} className="text-white">
                        {i + 1}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div>
              <Label className="text-slate-300">التوضيح (اختياري)</Label>
              <Textarea
                data-testid="explanation-input"
                value={formData.explanation}
                onChange={(e) => setFormData({ ...formData, explanation: e.target.value })}
                className="bg-slate-800 border-slate-700 text-white mt-1"
                rows={2}
              />
            </div>
            
            <div className="flex items-center gap-2">
              <Switch
                data-testid="is-active-switch"
                checked={formData.is_active}
                onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
              />
              <Label className="text-slate-300">السؤال مفعل</Label>
            </div>
            
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowQuestionModal(false)}
                className="border-slate-600 text-slate-300"
              >
                إلغاء
              </Button>
              <Button
                data-testid="save-question-btn"
                type="submit"
                className="bg-neon-cyan text-slate-900 hover:bg-neon-cyan-dim"
              >
                {editingQuestion ? "تحديث" : "إضافة"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Modal */}
      <Dialog open={showDeleteModal} onOpenChange={setShowDeleteModal}>
        <DialogContent className="bg-slate-900 border-slate-700 text-white" aria-describedby="delete-modal-description">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-red-400">
              تأكيد الحذف
            </DialogTitle>
          </DialogHeader>
          <p id="delete-modal-description" className="sr-only">تأكيد حذف السؤال</p>
          
          <p className="text-slate-300">
            هل أنت متأكد من حذف هذا السؤال؟ لا يمكن التراجع عن هذا الإجراء.
          </p>
          
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowDeleteModal(false)}
              className="border-slate-600 text-slate-300"
            >
              إلغاء
            </Button>
            <Button
              data-testid="confirm-delete-btn"
              onClick={handleDeleteQuestion}
              className="bg-red-600 hover:bg-red-700"
            >
              حذف
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Import Modal */}
      <Dialog open={showImportModal} onOpenChange={setShowImportModal}>
        <DialogContent className="bg-slate-900 border-slate-700 text-white" aria-describedby="import-modal-description">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">
              استيراد الأسئلة
            </DialogTitle>
          </DialogHeader>
          <p id="import-modal-description" className="sr-only">استيراد أسئلة من ملف</p>
          
          <div className="space-y-4">
            <div>
              <Label className="text-slate-300 block mb-2">ملف JSON</Label>
              <Input
                type="file"
                accept=".json"
                onChange={(e) => handleImport(e, "json")}
                className="bg-slate-800 border-slate-700 text-white"
              />
            </div>
            
            <div>
              <Label className="text-slate-300 block mb-2">ملف CSV</Label>
              <Input
                type="file"
                accept=".csv"
                onChange={(e) => handleImport(e, "csv")}
                className="bg-slate-800 border-slate-700 text-white"
              />
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
